/**
 * Packet Template Editor — view and edit the 19 parent packet form templates.
 * Admin/superadmin only. Route: /admin/packet-templates.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Alert,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import AppHeader from './AppHeader';
import { formsAPI, API_BASE_URL } from '../services/api';

const PREVIEW_STUDENT_ID_KEY = 'packet_template_preview_student_id';

// Content key -> human label for each form (matches backend content schema)
const CONTENT_LABELS = {
  form_01: { date: 'Date', signatory_name: 'Signatory Name', signatory_title: 'Signatory Title' },
  form_02: { signatory_name: 'Signatory Name', signatory_title: 'Signatory Title' },
  form_03: { open_school_night_date: 'Open School Night Date', parent_teacher_conference_date: 'Parent Teacher Conference Date' },
  form_04: { signatory_name: 'Signatory Name', signatory_title: 'Signatory Title' },
  form_05: { calendar_title: 'Calendar Title' },
  form_07: { authorization_text: 'Authorization Text', notes: 'Notes' },
  form_08: { notes: 'Notes' },
  form_09: { notes: 'Notes' },
  form_10: { photo_instruction: 'Photo Instruction' },
  form_11: { notes: 'Notes' },
  form_12: { notes: 'Notes' },
  form_13: { notes: 'Notes' },
  form_14: { notes: 'Notes' },
  form_15: { release_para1: 'Release Paragraph 1', release_para2: 'Release Paragraph 2', notes: 'Notes' },
  form_16: { notes: 'Notes' },
  form_17: { application_year: 'Application Year', school_name: 'School Name', school_address: 'School Address', meals_contact_name: 'Meals Contact Name', school_phone: 'School Phone', note: 'Generated single-page meals application' },
  form_18: { nurse_1_name: 'Nurse 1 Name', nurse_2_name: 'Nurse 2 Name', nurse_ext_1: 'Nurse Ext 1', nurse_3_name: 'Nurse 3 Name', nurse_ext_2: 'Nurse Ext 2', phone: 'Phone Number' },
  form_19: { confirmation_text: 'Confirmation Text', notes: 'Notes' },
};

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function PacketTemplates() {
  const [templates, setTemplates] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [content, setContent] = useState({});
  const [previewStudentId, setPreviewStudentId] = useState(() =>
    localStorage.getItem(PREVIEW_STUDENT_ID_KEY) || ''
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreviewSetting, setShowPreviewSetting] = useState(false);

  const loadTemplates = useCallback(async () => {
    setError('');
    try {
      const res = await formsAPI.getTemplates();
      const data = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
      setTemplates(data);
      if (data?.length) {
        setSelectedFormId((prev) => prev || data[0].form_id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!selectedFormId) return;
    setError('');
    setSelectedTemplate(null);
    formsAPI.getTemplate(selectedFormId)
      .then((res) => {
        setSelectedTemplate(res.data);
        setContent(res.data?.content ? { ...res.data.content } : {});
      })
      .catch((e) => setError(e.response?.data?.detail || e.message || 'Failed to load template'));
  }, [selectedFormId]);

  const handleSave = async () => {
    if (!selectedFormId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await formsAPI.updateTemplate(selectedFormId, { content });
      setSelectedTemplate(res.data);
      setSuccess('Saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    const sid = previewStudentId.trim();
    if (!sid) {
      setError('Set a preview student ID first (click the gear icon).');
      return;
    }
    setError('');
    try {
      const url = `${API_BASE_URL}/forms/preview/?student_id=${encodeURIComponent(sid)}&form_id=${encodeURIComponent(selectedFormId)}`;
      const token = localStorage.getItem('access_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(res.statusText || 'Preview failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (e) {
      setError(e.message || 'Preview failed. Check preview student ID and try again.');
    }
  };

  const savePreviewStudentId = () => {
    const sid = previewStudentId.trim();
    if (sid) localStorage.setItem(PREVIEW_STUDENT_ID_KEY, sid);
    setShowPreviewSetting(false);
  };

  const labels = selectedFormId ? (CONTENT_LABELS[selectedFormId] || {}) : {};
  const contentKeys = Object.keys(labels);

  return (
    <>
      <AppHeader />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Packet Templates</Typography>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>Forms</Typography>
                <List dense disablePadding>
                  {templates.map((t) => (
                    <ListItemButton
                      key={t.form_id}
                      selected={selectedFormId === t.form_id}
                      onClick={() => setSelectedFormId(t.form_id)}
                    >
                      <ListItemText primary={`${t.form_id.replace('_', ' ')} — ${t.form_name}`} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {selectedTemplate ? (
                  <>
                    <Typography variant="h6">{selectedTemplate.form_name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {selectedTemplate.form_id}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    {contentKeys.length === 0 ? (
                      <Typography color="text.secondary">No editable fields for this form.</Typography>
                    ) : (
                      <Box component="form" noValidate sx={{ mb: 2 }}>
                        {contentKeys.map((key) => (
                          <TextField
                            key={key}
                            fullWidth
                            multiline={key === 'release_para1' || key === 'release_para2' || key === 'confirmation_text' || key === 'checklist_intro' || key === 'photo_instruction' || key === 'authorization_text'}
                            rows={key === 'release_para1' || key === 'release_para2' || key === 'confirmation_text' ? 3 : key === 'checklist_intro' || key === 'photo_instruction' || key === 'authorization_text' ? 2 : 1}
                            label={labels[key]}
                            value={content[key] ?? ''}
                            onChange={(e) => setContent((prev) => ({ ...prev, [key]: e.target.value }))}
                            margin="normal"
                            size="small"
                          />
                        ))}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Saving…' : 'Save Changes'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={handlePreview}
                        disabled={!selectedFormId}
                      >
                        Preview
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => setShowPreviewSetting((s) => !s)}
                      >
                        Preview student ID
                      </Button>
                    </Box>

                    {showPreviewSetting && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Student ID for preview"
                          value={previewStudentId}
                          onChange={(e) => setPreviewStudentId(e.target.value)}
                          onBlur={savePreviewStudentId}
                          placeholder="e.g. 72"
                          sx={{ width: 180 }}
                        />
                        <Button size="small" onClick={savePreviewStudentId}>Save</Button>
                      </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      {selectedTemplate.last_edited_by
                        ? `Last edited by ${selectedTemplate.last_edited_by} on ${formatDateTime(selectedTemplate.last_edited_at)}`
                        : `Last edited: ${formatDateTime(selectedTemplate.last_edited_at)}`}
                    </Typography>
                  </>
                ) : (
                  <Typography color="text.secondary">Select a form from the list.</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
}
